"use client";

import { FC, useState } from "react";
import { Icon, OspToast } from "@ospweb/sdk-ui";
import { cn } from "utils";
import Translations from "@translate/components/Member/Subscription/UpgradeConfirmModal/UpgradeConfirmModalContent";
import { I18nUtil } from "@utils/i18n";
import type { MemberProduct } from "@features/api/memberApi";
import { pricingMoney } from "@components/Member/Subscription/onboardingPricingCopy";
import { CheckoutConsent } from "@components/Member/Subscription/SubscriptionCard/PurchaseActions/CheckoutConsent";
import { buildConsentCopy } from "@utils/member/checkoutConsent";
import {
  type ManagedPlanRef,
  useChangePlan,
} from "@hooks/useSubscriptionManage";

const I18n = I18nUtil.createScoped(Translations);

export type UpgradeConfirmView = "default" | "submitting" | "declined";

export type UpgradeConfirmResult =
  "succeeded" | "pending" | "declined_update_pm" | "cancelled" | "error";

export interface UpgradeConfirmModalContentProps {
  /** 当前订阅（换档对象） */
  currentPlan: ManagedPlanRef;
  currentPlanName: string;
  targetProduct: MemberProduct;
  onDone: (result: UpgradeConfirmResult) => void;
}

const PRIMARY_BUTTON_CLASS = "button md_button primary_button button_m w-full";
const SECONDARY_BUTTON_CLASS =
  "button md_button secondary_button button_m w-full";

/** 今日扣款 + 周期重置 + 剩余时长顺延（§7 R1：kmember 到期时间 = 原到期 + 购买天数）；每周期一整句，不拼接 */
const chargeCopy = (product: MemberProduct): string | null => {
  const amount = Number(product.price);
  if (!product.price || !Number.isFinite(amount)) {
    return null;
  }
  const vars = { price: pricingMoney(amount) };
  switch (product.pay_cycle) {
    case "YEAR":
      return I18n.t(
        "You'll be charged %{price} today with your saved payment method. Your new yearly billing cycle starts today.",
        vars,
      );
    case "MONTH":
      return I18n.t(
        "You'll be charged %{price} today with your saved payment method. Your new monthly billing cycle starts today.",
        vars,
      );
    case "WEEK":
      return I18n.t(
        "You'll be charged %{price} today with your saved payment method. Your new weekly billing cycle starts today.",
        vars,
      );
    default:
      return null;
  }
};

/**
 * 一键升级确认（EDG-552，fe-spec §4.6 F10）。弹窗内持有 view 状态并调用换档接口：
 * SUCCEEDED / PENDING / 错误 → 关闭并交给调用方；REQUIRES_PAYMENT_METHOD → 原地切被拒态（不自动重扣，AC-38）。
 * 提交中隐藏关闭入口（关闭按钮由内容区自绘，EdgenModal 的 showClose 是调用时定死的）。
 */
export const UpgradeConfirmModalContent: FC<
  UpgradeConfirmModalContentProps
> = ({ currentPlan, currentPlanName, targetProduct, onDone }) => {
  const [view, setView] = useState<UpgradeConfirmView>("default");
  const { changePlan } = useChangePlan();
  const targetPlanName = targetProduct.name ?? "";
  const isSubmitting = view === "submitting";
  const confirmLabel = I18n.t("Confirm upgrade");
  const charge = chargeCopy(targetProduct);
  // 扣款金额或同意条款任一缺失都不能让用户确认 —— 不展示金额就扣款不可接受（Review Issue #11）
  const canConfirm =
    !!charge &&
    !!buildConsentCopy({ product: targetProduct, ctaLabel: confirmLabel });

  const handleConfirm = async () => {
    const targetProductId = targetProduct.id;
    if (!targetProductId || !canConfirm) {
      return;
    }
    setView("submitting");
    const outcome = await changePlan(currentPlan, {
      ...targetProduct,
      id: targetProductId,
    });
    switch (outcome.status) {
      case "SUCCEEDED":
        onDone("succeeded");
        return;
      case "PENDING":
        onDone("pending");
        return;
      case "REQUIRES_PAYMENT_METHOD":
        setView("declined");
        return;
      default:
        OspToast.error({ content: outcome.message });
        onDone("error");
    }
  };

  return (
    <div
      className="relative flex flex-col gap-4 p-4 md:p-6"
      data-or-view="subscription_surface_view"
      data-or-param-surface="upgrade_confirm"
      data-or-param-planid={currentPlan.id}
      data-or-param-channel={currentPlan.pay_channel}
    >
      {!isSubmitting && (
        <button
          type="button"
          data-openreplay-label="member_upgradeconfirm_close"
          aria-label={I18n.t("Close")}
          className="absolute top-3 right-3 z-10 flex items-center justify-center"
          onClick={() => onDone("cancelled")}
        >
          <Icon
            name="close"
            size={24}
            className="text-content_primary cursor-pointer button_icon"
          />
        </button>
      )}
      <div className="text-content_primary headline_regular_s md:headline_regular_m pr-8">
        {I18n.t("Upgrade to %{plan}", { plan: targetPlanName })}
      </div>
      <p className="body_medium_m text-content_secondary">
        {currentPlanName} → {targetPlanName}
      </p>
      {view === "declined" ? (
        <p className="body_m text-content_accent2_primary">
          {I18n.t(
            "Your card was declined. Update your payment method, then try upgrading again.",
          )}
        </p>
      ) : (
        <>
          <p className="body_m text-content_primary">
            {charge}{" "}
            {I18n.t(
              "Any unused time on your current plan will be added to the end of your new plan.",
            )}
          </p>
          <CheckoutConsent product={targetProduct} ctaLabel={confirmLabel} />
        </>
      )}
      <div className="flex flex-col gap-2">
        {view === "declined" ? (
          <>
            <button
              type="button"
              data-openreplay-label="member_upgradeconfirm_update_pm"
              data-or-event="subscription_action_click"
              data-or-param-action="update_pm"
              data-or-param-planid={currentPlan.id}
              data-or-param-channel={currentPlan.pay_channel}
              data-or-param-position="pricing_card"
              className={PRIMARY_BUTTON_CLASS}
              onClick={() => onDone("declined_update_pm")}
            >
              {I18n.t("Update payment method")}
            </button>
            <button
              type="button"
              data-openreplay-label="member_upgradeconfirm_dismiss"
              className={SECONDARY_BUTTON_CLASS}
              onClick={() => onDone("cancelled")}
            >
              {I18n.t("Close")}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              data-openreplay-label="member_upgradeconfirm_confirm"
              data-or-event="subscription_action_click"
              data-or-param-action="upgrade_confirm"
              data-or-param-planid={currentPlan.id}
              data-or-param-channel={currentPlan.pay_channel}
              data-or-param-position="pricing_card"
              disabled={isSubmitting || !canConfirm}
              data-loading={isSubmitting}
              className={cn(PRIMARY_BUTTON_CLASS, "group")}
              onClick={() => void handleConfirm()}
            >
              <span className="group-loading:hidden">{confirmLabel}</span>
              <Icon
                name="ic_loading2"
                size={20}
                className="hidden animate-spin group-loading:block"
              />
            </button>
            <button
              type="button"
              data-openreplay-label="member_upgradeconfirm_cancel"
              data-or-event="subscription_action_click"
              data-or-param-action="upgrade_cancel"
              data-or-param-planid={currentPlan.id}
              data-or-param-channel={currentPlan.pay_channel}
              data-or-param-position="pricing_card"
              disabled={isSubmitting}
              className={SECONDARY_BUTTON_CLASS}
              onClick={() => onDone("cancelled")}
            >
              {I18n.t("Cancel")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
